import { useCallback, useEffect, useRef } from "react";
import Translate, { TranslateEvent } from "ol/interaction/Translate";
import { Collection } from "ol";
import Map from "ol/Map";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";

/**
 * OL Translate 인터랙션의 설치/해제 라이프사이클을 관리합니다.
 * install(feature)로 이동 핸들을 붙이고, remove()로 제거합니다.
 */
export function useTranslateInteraction(
  map: Map,
  onTranslateEnd: (event: TranslateEvent) => void
) {
  const translateRef = useRef<Translate | null>(null);

  // 안정적인 래퍼를 통해 최신 콜백을 항상 호출 (OL 이벤트 재등록 불필요)
  const onTranslateEndRef = useRef(onTranslateEnd);
  onTranslateEndRef.current = onTranslateEnd;

  const stableOnTranslateEnd = useCallback(
    (e: TranslateEvent) => onTranslateEndRef.current(e),
    []
  );

  const remove = useCallback(() => {
    if (translateRef.current) {
      translateRef.current.un("translateend", stableOnTranslateEnd);
      map.removeInteraction(translateRef.current);
      translateRef.current = null;
    }
  }, [map, stableOnTranslateEnd]);

  const install = useCallback(
    (feature: Feature<Geometry>) => {
      remove();

      const features = new Collection([feature]);
      translateRef.current = new Translate({ features });
      translateRef.current.on("translateend", stableOnTranslateEnd);
      map.addInteraction(translateRef.current);
    },
    [map, remove, stableOnTranslateEnd]
  );

  useEffect(() => () => remove(), [remove]);

  return { install, remove };
}
